import { createFileRoute, Link } from "@tanstack/react-router"
import { Target } from "lucide-react"
import { useMemo } from "react"
import z from "zod"
import { Page } from "@/components/page"
import { Button } from "@/components/ui/button"
import { EstateTotals } from "@/components/ui/estate-totals"
import { Typography } from "@/components/ui/typography"
import { STEPS } from "@/constants/steps"
import { trpc } from "@/lib/trpc"
import { GuidedForm } from "../../../../components/forms/guided-form"

type StepId = (typeof STEPS)[number]["id"]

export const searchSchema = z.object({
  currentStep: z
    .enum([...STEPS.map((s) => s.id)] as [StepId, ...StepId[]])
    .optional()
    .catch("intro"),
  tab: z.string().optional(),
  journalId: z.string().optional(),
})

export const Route = createFileRoute("/_authed/_individual/personal/guided-forms")({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
})
function RouteComponent() {
  const search = Route.useSearch()

  // Get all estates the individual has access to
  const {
    data: estates = [],
    isLoading: estatesLoading,
    error: estatesError,
  } = trpc.estates.list.useQuery()

  // Pick a "primary" estate:
  //    - Prefer an Admin, non-journal-only estate
  //    - Fallback to the first estate if nothing matches
  const primaryEstate = useMemo(() => {
    if (!estates.length) return undefined
    const adminNonJournal = estates.find((e) => e.accessLevel === "Admin" && !e.journalOnly)
    return adminNonJournal ?? estates[0]
  }, [estates])

  return (
    <Page
      headerSlot={
        <div className="flex items-center justify-between">
          <Typography as="h1" size="2xl">
            Guided Forms
          </Typography>
          <Button variant="link" asChild className="px-0">
            <Link to="/personal/goals">
              <Target className="size-4 mr-2" />
              Go to Goals
            </Link>
          </Button>
        </div>
      }
    >
      {!estatesLoading && !estatesError && primaryEstate && (
        <div className="mb-6 flex justify-center lg:justify-end">
          <EstateTotals estateId={primaryEstate.id} />
        </div>
      )}
      {/* Guided form */}
      <div className="mb-8">
        <GuidedForm currentStep={search.currentStep ?? "intro"} />
      </div>
    </Page>
  )
}
